import React from 'react';
import CloseIcon from '@mui/icons-material/Close'; // MUI Close icon (or use your own SVG if preferred)
import Close from '@mui/icons-material/Close';
import Printicon from "@mui/icons-material/Print";
import { Box, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';

// NOTE: The 'apiData' structure is assumed to be an object containing the keys: 


const PackingListReport = ({ apiData = {}, callback }) => {
    // Determine the actual API data source (using props, or fallback to an empty structure)
    const dataToUse = apiData[0] || {}; // Assuming the structure is [{...}]

    // --- SAFE DATA EXTRACTION ---
    const header = dataToUse.header?.[0] || {};
    const dispatchPlanItem = dataToUse.dispatch_plan?.[0] || {};
    const itemsData = dataToUse.items || [];

    // --- 1. ITEMS MAPPING (Direct mapping from 'items' + 'dispatch_plan') ---
    const structuredItems = itemsData.map((item, index) => {
        // Source for most common/shared fields is dispatchPlanItem
        const orderNo = dispatchPlanItem.deliveryorderno || '';
        const partyPoNo = dispatchPlanItem.partypono || '';
        const uom = dispatchPlanItem.uom || '';

        // Fields that might be on the item OR fallback to dispatchPlanItem (e.g., if items don't store them all)
        const noOfSheets = item.noofsheets || dispatchPlanItem.noofsheets || '';
        const reamWt = item.reamweight || dispatchPlanItem.reamweight || '';
        const reamsPerBundle = item.reamsperbundle || dispatchPlanItem.reamsperbundle || '';
        const nos = item.noofbundlesreels || dispatchPlanItem.noofbundlesreels || ''; // nos (No of Bundles/Reels)

        // Fields primarily from the item line (or fallback)
        const quality = item.variety || dispatchPlanItem.variety || '';
        const gsm = item.gsm || dispatchPlanItem.gsm || '';
        const size = `${item.width || dispatchPlanItem.width || ''} x ${item.length || dispatchPlanItem.length || ''}`.trim();
        const reelNo = item.reelnumber || '';

        // Weight/Quantity is item-specific
        const itemWeight = item.qty ? parseFloat(item.qty) : 0;

        return {
            sno: (index + 1).toString(), // Using calculated index for S.No
            orderNo: orderNo,
            partyPoNo: partyPoNo,
            quality: quality,
            gsm: gsm,
            size: size,
            specialInstruction: item.specialInstruction || '',
            noOfSheets: noOfSheets,
            reamWt: reamWt,
            reamsPerBundle: reamsPerBundle,
            reelNo: reelNo,
            nos: nos,
            uom: uom,
            weight: itemWeight.toFixed(3).toString()
        };
    });

    // --- 2. TOTALS CALCULATION ---
    const totalQuantity = structuredItems.reduce((sum, item) => {
        const weight = parseFloat(item.weight) || 0;
        return sum + weight;
    }, 0);


    // --- 3. FINAL DATA STRUCTURE ---
    const data = {
        Company: {
            name: header.companyname || '',
            addressLine1: header.company_addrline1 || '',
            addressLine2: `${header.company_addrline2 || ''} ${header.company_addrline3 || ''}`.trim(),
            email: header.billto_addremailid1 || '',
            gstin: header.company_gstno || '',
            title: "Delivery Packing List"
        },
        Header: {
            invoiceNo: header.invoice_no || '',
            invoiceDate: header.invoice_date || '',
            deliveryNo: header.shippedplanorderno || '',
            date: header.shippedplandate || '',
            truckNumber: header.trucknumber || '',
            driverContact: header.drivercontactno || '',
            lrNo: header.lrno || '',
            freight: header.freightplan || ''
        },
        ShipTo: {
            name: header.shiptoname || '',
            addressLine1: header.shipto_addrline1 || '',
            addressLine2: `${header.shipto_addrline2 || ''} ${header.shipto_addrcity || ''}`.trim(),
            tel: header.shipto_addrtelno1 || '',
            email: header.shipto_addremailid1 || '',
            gstin: header.shipto_gstno || '',
            code: header.shipto_gststatecode || ''
        },
        BillTo: {
            name: header.billtoname || '',
            addressLine1: header.billto_addrline1 || '',
            addressLine2: `${header.billto_addrline2 || ''} ${header.billto_addrcity || ''}`.trim(),
            tel: header.billto_addrtelno1 || '',
            email: header.billto_addremailid1 || '',
            gstin: header.billto_gstno || '',
            code: header.billto_gststatecode || ''
        },
        Items: structuredItems,
        Totals: {
            subTotal: totalQuantity.toFixed(2),
            totalQty: totalQuantity.toFixed(2),
            uom: structuredItems[0]?.uom || 'KG'
        },
        Footer: "Page 1 of 1"
    }

    const handlePrint = () => {
        const printContent = document.getElementById('packing-list-report').innerHTML;
        const printWindow = window.open('', '', 'height=800,width=1200');
        printWindow.document.write('<html><head><title>Packing List</title>');
        printWindow.document.write(`
            <style>
                body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
                .report-container { width: 210mm; margin: auto; padding: 12mm; font-size: 10px; }
                .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 0px; }
                .header h2, .header p { margin: 0; padding: 0; }
                .header h2 { font-size: 1.5em; }
                .header p { font-size: 1em; margin-top: 5px; }
                .header-block {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 10px;
                    padding: 5px;
                    border: 1px solid black;
                    margin-bottom: 0px;
                }
                .invoice-details, .truck-details {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .bill-ship-container {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 10px;
                    margin-bottom: 0px;
                }
                .contact-block {
                    border: 1px solid black;
                    padding: 5px;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 10px;
                }
                th, td {
                    border: 1px solid black;
                    padding: 5px;
                    text-align: left;
                    font-size: 9px;
                }
                th {
                    background-color: #f2f2f2;
                    text-align: center;
                }
                .totals-row td {
                    font-weight: bold;
                    text-align: right;
                }
                .footer {
                    text-align: right;
                    margin-top: 10px;
                }
            </style>
        `);
        printWindow.document.write('</head><body>');
        printWindow.document.write(printContent);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };
    const handleClose = () => {
        if (callback) callback(false);
    };

    return (
        <>
            <Grid container justifyContent="space-between" alignItems="center" padding={0}>
                {/* Left-aligned: Edit Employee */}
                <Grid item>
                    <Typography variant="h4" sx={{ textAlign: "center", width: "100%", marginLeft: "10px", alignItems: "center" }}>
                        Delivery Packing List
                    </Typography>
                </Grid>

                {/* Right-aligned: Buttons */}
                <Grid item>
                    <Grid container alignItems="center">
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        {/* Save */}
                        <Grid item ml={1} mr={1}>
                            <Tooltip title="Add new orders">
                                <IconButton
                                    onClick={() => handlePrint()}
                                    sx={{ display: 'flex', alignItems: 'center' }}
                                // type="submit"
                                >
                                    <Printicon style={{ fontSize: "18px", color: "#009688" }} />
                                    <Typography variant="body2" sx={{ ml: 1 }}> Print</Typography>
                                </IconButton>
                            </Tooltip>
                        </Grid>

                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                        <IconButton onClick={() => { handleClose() }}>
                            <Close style={{ fontSize: "14px", color: "#2196F3" }} />
                        </IconButton>

                        <Divider orientation="vertical" flexItem />

                    </Grid>
                </Grid>
            </Grid>

            <Divider />
            <Box>
                <div id="packing-list-report" style={{ border: '1px solid black', padding: '15px', maxWidth: '210mm', margin: 'auto' }}>

                    {/* Header Section */}
                    <div style={{ marginBottom: "0px", borderBottom: "2px solid #000" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "5px" }}>
                            <div style={{ textAlign: "left" }}>
                                <img src="/path/to/your/fsc-logo.png" alt="FSC Logo" style={{ width: "80px", marginBottom: "5px" }} />
                                <p style={{ margin: 0, fontSize: "10px", whiteSpace: "nowrap" }}>
                                    www.fsc.org<br />
                                    FSC® C210691<br />
                                    The mark of responsible forestry
                                </p>
                            </div>
                            <div style={{ textAlign: "center", flexGrow: 1 }}>
                                <h3 style={{ margin: "0", whiteSpace: "nowrap" }}>{data.Company.name}</h3>
                                <div style={{ whiteSpace: "nowrap" }}>{data.Company.addressLine1}</div>
                                <div style={{ whiteSpace: "nowrap" }}> {data.Company.addressLine2}</div>
                                <div style={{ whiteSpace: "nowrap" }}> Email ID: {data.Company.email}</div>
                                <div style={{ whiteSpace: "nowrap" }}>GSTIN:{data.Company.gstin}</div>
                            </div>
                            <div style={{ width: "80px" }}></div>
                        </div>
                    </div>
                    <div style={{ textAlign: "center", marginBottom: "0px", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
                        <h2 style={{ margin: "0" }}>{data.Company.title}</h2>
                    </div>
                    {/* Main 3-Partition Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', border: '1px solid black', borderBottom: '1px solid black', marginTop: '0px' }}>
                        {/* Partition 1: Bill To */}
                        <div style={{ borderRight: '1px solid black', padding: '10px' }}>
                            <div><b>Bill to Name & Address</b></div>
                            <div>{data.BillTo.name}</div>
                            <div>{data.BillTo.addressLine1}, {data.BillTo.addressLine2}</div>
                            <div>Tel: {data.BillTo.tel}</div>
                            <div>Email ID: {data.BillTo.email}</div>
                            <div>GSTIN: {data.BillTo.gstin}</div>
                            <div>Code: {data.BillTo.code}</div>
                        </div>
                        {/* Partition 2: Ship To */}
                        <div style={{ borderRight: '1px solid black', padding: '10px' }}>
                            <div><b>Ship to Name & Address</b></div>
                            <div>{data.ShipTo.name}</div>
                            <div>{data.ShipTo.addressLine1}, {data.ShipTo.addressLine2}</div>
                            <div>Tel: {data.ShipTo.tel}</div>
                            <div>Email ID: {data.ShipTo.email}</div>
                            <div>GSTIN: {data.ShipTo.gstin}</div>
                            <div>Code: {data.ShipTo.code}</div>
                        </div>
                        {/* Partition 3: Header/Shipment Details */}
                        <div style={{ padding: '10px' }}>

                            <div><b>Invoice No:</b> {data.Header.invoiceNo}</div>
                            <div><b>Invoice Date:</b> {data.Header.invoiceDate}</div>
                            <div><b>Delivery No:</b> {data.Header.deliveryNo}</div>
                            <div><b>Date:</b> {data.Header.date}</div>

                            <div><b>Truck Number:</b> {data.Header.truckNumber}</div>
                            <div><b>Driver Contact:</b> {data.Header.driverContact}</div>
                            <div><b>LR No:</b> {data.Header.lrNo}</div>
                            <div><b>Freight:</b> {data.Header.freight}</div>
                        </div>
                    </div>

                    {/* Items Table */}
                    <table style={{
                        marginTop: '0px',
                        borderCollapse: 'collapse',
                        width: '100%',
                        borderLeft: '1px solid black',
                        borderRight: '1px solid black',
                        borderBottom: '1px solid black'
                    }}>
                        <thead>
                            <tr>
                                <th style={{ border: '1px solid black' }}>S. No</th>
                                <th style={{ border: '1px solid black' }}>Order No</th>
                                <th style={{ border: '1px solid black' }}>Party PO No.</th>
                                <th style={{ border: '1px solid black' }}>Quality</th>
                                <th style={{ border: '1px solid black' }}>GSM</th>
                                <th style={{ border: '1px solid black' }}>Size (inches)</th>
                                <th style={{ border: '1px solid black' }}>Special Instruction</th>
                                <th style={{ border: '1px solid black' }}>No of Sheets</th>
                                <th style={{ border: '1px solid black' }}>Ream Wt (Kg)</th>
                                <th style={{ border: '1px solid black' }}>Reams / Bundle</th>
                                <th style={{ border: '1px solid black' }}>Reel No</th>
                                <th style={{ border: '1px solid black' }}>Nos</th>
                                <th style={{ border: '1px solid black' }}>UoM</th>
                                <th style={{ border: '1px solid black' }}>Weight</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.Items.map((item, index) => (
                                <tr key={index}>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.sno}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.orderNo}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.partyPoNo}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.quality}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.gsm}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.size}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.specialInstruction}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.noOfSheets}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.reamWt}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.reamsPerBundle}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.reelNo}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.nos}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.uom}</td>
                                    <td style={{ textAlign: 'center', borderRight: '1px solid black' }}>{item.weight}</td>
                                </tr>
                            ))}
                            <tr className="totals-row">
                                <td colSpan="12" style={{ textAlign: 'right', border: '1px solid black' }}>Sub Total</td>
                                <td style={{ textAlign: 'center', border: '1px solid black' }}>{data.Totals.uom}</td>
                                <td style={{ textAlign: 'center', border: '1px solid black' }}>{data.Totals.subTotal}</td>
                            </tr>
                            <tr className="totals-row">
                                <td colSpan="13" style={{ textAlign: 'right', border: '1px solid black' }}>Total Qty ({data.Totals.uom})</td>
                                <td style={{ textAlign: 'center', border: '1px solid black' }}>{data.Totals.totalQty}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Footer */}
                    <div className="footer">
                        <div>{data.Footer}</div>
                    </div>
                </div>
            </Box>
        </>
        
    );
};

export default PackingListReport;